import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

/**
 * Bahasa tampilan website: "id" (Indonesia, default) atau "en" (English).
 *
 * Dipakai bersama oleh lib/homePageContent.ts (memilih field `<key>` vs
 * `<key>_en` dari konten CMS) dan komponen Navbar (switcher-nya).
 */
export type Language = "id" | "en";

const STORAGE_KEY = "arunika_language";

interface LanguageContextValue {
  language: Language;
  setLanguage: (language: Language) => void;
  toggleLanguage: () => void;
}

const readStoredLanguage = (): Language => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "en" ? "en" : "id";
  } catch {
    // localStorage diblokir (mis. private browsing ketat) -> default id.
    return "id";
  }
};

const writeStoredLanguage = (language: Language) => {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Abaikan -- pilihan bahasa tetap berfungsi untuk sesi berjalan,
    // hanya tidak tersimpan untuk kunjungan berikutnya.
  }
};

const LanguageContext = createContext<LanguageContextValue>({
  language: "id",
  setLanguage: () => {},
  toggleLanguage: () => {},
});

/**
 * Menyimpan pilihan bahasa pengunjung untuk SELURUH website (bukan cuma
 * Home/About Us), persist lintas kunjungan lewat localStorage (beda dengan
 * cache konten Home Page yang sengaja per-sesi/sessionStorage).
 */
export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(readStoredLanguage);

  const setLanguage = (next: Language) => {
    setLanguageState(next);
    writeStoredLanguage(next);
  };

  const toggleLanguage = () => setLanguage(language === "id" ? "en" : "id");

  // Aksesibilitas & SEO: elemen <html lang="..."> ikut bahasa yang dipilih.
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
